import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { createRoomShare } from "@/lib/api";
import { trackRoomShared } from "@/lib/analytics";

/**
 * RoomShareDialog — emails the visitor the room they just built and files the
 * lead in the Studio Inbox.
 */
export default function RoomShareDialog({ open, onOpenChange, room, spec, imageUrl }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await createRoomShare({
        name: form.name.trim(),
        email: form.email.trim(),
        room,
        spec,
        image_url: imageUrl || null,
      });
      trackRoomShared({ room, spec });
      toast.success("Sent", { description: "Your room is on its way to your inbox." });
      setForm({ name: "", email: "" });
      onOpenChange(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="room-share-dialog"
        className="bg-[#121315] border border-[#8C7355]/25 text-[#F5F4F0] sm:max-w-lg p-8 md:p-10"
      >
        <DialogHeader>
          <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase">Your room</p>
          <DialogTitle className="font-serif-display italic font-light tracking-tight text-3xl md:text-4xl text-[#F5F4F0]">
            Email this to yourself
          </DialogTitle>
          <DialogDescription className="text-[#D6D1CA]/55 font-body">
            {spec} — we'll send the render and the specification so you have it when you talk to
            your builder.
          </DialogDescription>
        </DialogHeader>

        {imageUrl && imageUrl.startsWith("/") && (
          <img
            src={imageUrl}
            alt={spec}
            className="w-full aspect-[3/2] object-cover border border-[#8C7355]/25 mt-2"
          />
        )}

        <form onSubmit={submit} className="mt-5 space-y-6" data-testid="room-share-form">
          <input
            data-testid="room-share-name"
            className="field-underline w-full"
            placeholder="Full name"
            value={form.name}
            onChange={set("name")}
          />
          <input
            data-testid="room-share-email"
            type="email"
            className="field-underline w-full"
            placeholder="Email address"
            value={form.email}
            onChange={set("email")}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="room-share-submit"
            className="btn-bronze inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-[11px] tracking-[0.22em] uppercase font-medium disabled:opacity-60"
          >
            <Mail size={13} strokeWidth={1.8} />
            {loading ? "Sending…" : "Send me this room"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
